import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from "@revbox/helpers-fe";

const resizeImage = (imageBlob, width, height) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(resolve, "image/jpeg", 0.95);
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(imageBlob);
  });
};

const ImageCropModal = React.forwardRef(({
  modalShow,
  modalHandleClose,
  imageFiles,
  onSave,
  dimensions,
  secondaryDimensions = null
}, ref) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(3);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);

  const { width, height } = dimensions;
  const aspectRatio = width / height;

  // Розміри області кропу - адаптивні
  const cropWidth = Math.min(width, 800);
  const cropHeight = Math.min(height, 450);

  const handleMediaLoaded = useCallback((mediaSize) => {
    const { width: imgW, height: imgH } = mediaSize;
    
    // Обчислюємо мінімальний зум, щоб зображення заповнило всю область кропу
    const scaleX = cropWidth / imgW;
    const scaleY = cropHeight / imgH;
    const minScale = Math.max(scaleX, scaleY);
    
    // Встановлюємо межі зуму
    setMinZoom(minScale);
    setMaxZoom(Math.max(minScale * 3, 3)); // Максимум в 3 рази більше мінімального
    
    // Початковий зум = мінімальний, щоб зображення заповнило область
    setZoom(minScale);
    setCrop({ x: 0, y: 0 });
  }, [cropWidth, cropHeight]);

  useEffect(() => {
    if (imageFiles && imageFiles?.length > 0 && currentImageIndex < imageFiles?.length) {
      const imageFile = imageFiles[currentImageIndex];
      const objectUrl = URL.createObjectURL(imageFile);
      setImageSrc(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [imageFiles, currentImageIndex]);

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const resizedImageBlob = await resizeImage(croppedImageBlob, width, height);
      const processedFiles = [resizedImageBlob]

      if (secondaryDimensions) {
        const secondaryCroppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        const secondaryResizedImageBlob = await resizeImage(secondaryCroppedImageBlob, secondaryDimensions.width, secondaryDimensions.height);
        processedFiles.push(secondaryResizedImageBlob)
      }

      const resizedFiles = []

      for (const file of processedFiles) {
        const resizedFile = new File([file], imageFiles[currentImageIndex].name, {
          type: imageFiles[currentImageIndex].type
        })
        resizedFiles.push(resizedFile)
      }

      onSave(secondaryDimensions? resizedFiles : resizedFiles[0], currentImageIndex);

      if (currentImageIndex < imageFiles?.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
      } else {
        modalHandleClose();
        setCurrentImageIndex(0)
      }
    } catch (error) {
      console.error("Error cropping or resizing the image:", error);
    }
  };

  return (
    <Modal
      show={modalShow}
      onHide={() => {
        if (ref.current) {
          ref.current.value = "";
        }
        modalHandleClose();
      }}
      size="xl"
      className="image-crop-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Crop this image ({currentImageIndex + 1} / {imageFiles?.length})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ position: "relative", width: "100%", height: "500px" }}>
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              maxZoom={maxZoom}
              minZoom={minZoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              restrictPosition={true}
              onMediaLoaded={handleMediaLoaded}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              cropSize={{ width: cropWidth, height: cropHeight }}
            />
          ) : (
            <p>Loading image...</p>
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-3" style={{ width: "100%" }}>
          <Form.Group as={Row} className="align-items-center" style={{ flex: 1 }}>
            <Col xs="auto">
              <Form.Label id="Zoom" style={{ color: "black" }}>Zoom</Form.Label>
            </Col>
            <Col>
              <Form.Range
                value={zoom}
                min={minZoom}
                max={maxZoom}
                step={0.1}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                aria-labelledby="Zoom"
              />
            </Col>
          </Form.Group>

          <Button
            style={{ marginLeft: "40px" }}
            variant="outline-primary"
            size="sm"
            onClick={handleSave}
          >
            {`Crop & Save (${currentImageIndex + 1} / ${imageFiles?.length})`}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
});

export default ImageCropModal;